import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { Link, withRouter } from 'react-router-dom';
import appConfig from 'app-config';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { PolicyChangeService } from 'gw-capability-gateway';
import { PolicyDiffService } from 'gw-capability-policyjob';
import { FormModalPopover } from 'ml-components-platform-react';
import { MLLocalDateUtil } from 'ml-portals-util-js';

import mlDiffDialogModalMessages from './MLDiffDialogModal.messages';
import mlDiffDialogModal from './MLDiffDialogModal.metadata.json5';
import mlDiffDialogStyles from './MLDiffDialogModal.module.scss';

import styles from '../ActivityReportsComponent/ActivityReportsComponent.module.scss';
import messages from './MLPolicyChangesReportComponent.messages';
import metadata from './MLPolicyChangesReportComponent.metadata.json5';
import MLPolicyChangesAdditionalFilter from "./MLPolicyChangesAdditionalFilter";

import { Button } from '@jutro/legacy/components';

const CONSUMER_POLICY_CHANGE_REPORT = 'consumer-policy-change-report';
const PRODUCER_POLICY_CHANGE_REPORT = 'producer-policy-change-report';
const POLICY_CHANGES_REPORT = 'policy-changes-report';

const MLPolicyChangesReportComponent = (props) => {
    const modalApi = useModal();
    const {
        showAgentChanges,
        showAllChanges,
        rowLimit,
        isDashboard,
        isCompactView,
        getCustomerDisplayNameRevert,
        history
    } = props;
    const { gatewayParamConfig } = appConfig;
    const translator = useTranslator();

    const { authHeader } = useAuthentication();

    const [isLoading, updateIsLoading] = useState(false);
    const [filteredJobs, updateFilteredJobs] = useState([]);
    const [resultsRowCount, updateResultsRowCount] = useState(0);
    const [filterData, updateFilterData] = useState({});

    const loadPolicyChangeData = useCallback(async (filteringData) => {
        updateIsLoading(true);

        const response = isDashboard
            ? await PolicyChangeService.getBoundPolicyChangesForCurrentUser(gatewayParamConfig.jobsCreatedInLastXDays, filteringData, showAgentChanges, showAllChanges, rowLimit, authHeader)
            : await PolicyChangeService.getBoundPolicyChangesForCurrentUser(0, filteringData, showAgentChanges, showAllChanges, null, authHeader);

        updateFilteredJobs(response);
        updateIsLoading(false);
        updateResultsRowCount(response.length);
    }, [authHeader, gatewayParamConfig.jobsCreatedInLastXDays, isDashboard, rowLimit, showAgentChanges, showAllChanges]);

    useEffect(() => {
        loadPolicyChangeData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAgentChanges, showAllChanges]);

    const onFilter = () => {
        loadPolicyChangeData(filterData);
    };

    const onClearFilters = () => {
        updateFilterData({});
    };

    const renderDateCell = (item) => {
        return item.closeDate ? moment(item.closeDate).format('MM/DD/YYYY') : '-';
    };

    const renderCustomerName = (item) => {
        if(_.isUndefined(item.accountHolder_ML.lastName)) {
            return '-'
        }
        return (
            <Link to={`/accounts/${item.accountNumber}/summary`} className={styles.linkCellValue}>
                {_.capitalize(getCustomerDisplayNameRevert(item.accountHolder_ML))}
            </Link>
        )
    };

    const renderProductCell = (item) => {
        return item.product.productName ? item.product.productName : '-';
    };

    const renderPolicyCell = (item) => {
        if(_.isUndefined(item.policyNumber)) {
            return '-'
        }
        return (
            <Link to={`/policies/${item.policyNumber}/summary`} className={styles.linkCellValue}>
                {item.policyNumber}
            </Link>
        )
    };

    const renderInitiatorCell = (item) => {
        return item.changeInitiator_Ext ? item.changeInitiator_Ext : '-';
    };

    const renderDescriptionCell = (item) => {
        const description = item.description ? item.description : translator(messages.clickForDetails)

        const getDiffToPrevious = () => {
            PolicyDiffService.getDiffToPrevious(item.jobNumber, item.policyNumber, authHeader).then((result) => {
                const transactions = [' ', ...result.jobTransactions];
                const diffs = result.diffNodes;

                const generateOverrides = () => {
                    const headerOverrides = transactions.map((transaction, index) => {
                        const formattedDate = transaction !== ' ' ? MLLocalDateUtil.localDate2Date(transaction.effDate, true) : undefined;
                        return {
                            [`header${index}`]: {
                                content: formattedDate && translator(mlDiffDialogModalMessages.policyEffectiveDate, { date: formattedDate })
                            }
                        };
                    });

                    const contentOverrides = diffs.map((diff, index) => {
                        return {
                            [`itemLabel${index}`]: {
                                content: diff.label
                            },
                            [`value1${index}`]: {
                                content: diff.value1
                            },
                            [`value2${index}`]: {
                                content: diff.value2
                            }
                        }
                    });
                    return Object.assign({}, ...headerOverrides, ...contentOverrides);
                };

                const componentProps = {
                    title: mlDiffDialogModalMessages.yourComparison,
                    uiProps: mlDiffDialogModal.componentContent,
                    cancelBtnLabel: mlDiffDialogModalMessages.closeModal,
                    modalBodyStyles: mlDiffDialogStyles,
                    footerBtnRight: true,
                    showActionButton: false,
                    isGPA: true,
                    getOverrideProps: () => {
                        return {
                            headerIterables: {
                                data: transactions
                            },
                            tableRowIterable: {
                                data: diffs
                            },
                            ...generateOverrides()
                        };
                    }
                };

                modalApi.showModal(
                    <FormModalPopover {...componentProps} />
                );
            });
        };

        return (
            <Button className={styles.descriptionButton} onClick={getDiffToPrevious} type="text" style={{'text-transform': 'unset'}}>{description}</Button>
        )
    };

    const onViewAllChangesButtonClicked = () => {
        let reportView;

        if (showAllChanges) {
            reportView = POLICY_CHANGES_REPORT;
        } else {
            reportView = showAgentChanges ? PRODUCER_POLICY_CHANGE_REPORT : CONSUMER_POLICY_CHANGE_REPORT;
        }

        history.push('/activity-reports', {reportView : reportView} );
    }

    const overrideProps = {
        mlJobResultsFilterComponent: {
            visible: !isCompactView,
            filterData,
            onFilter,
            onClearFilters,
            resultsRowCount,
            updateFilterData
        },
        policyChangesResultsFilterComponent: {
            visible: !isDashboard,
            filterData,
            onFilter,
            onClearFilters,
            resultsRowCount,
            updateFilterData,
            additionalFiltersComponent: MLPolicyChangesAdditionalFilter,
        },
        policyChangesReportTable: {
            data: _.orderBy(filteredJobs, 'closeDate', 'asc'),
            visible: !isLoading,
            defaultPageSize: rowLimit,
            noDataText: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.noRecentChangesFound)}</span>),
            showPagination: !isCompactView,
            tableLabel: showAgentChanges ? translator(messages.agentChanges) : translator(messages.customerChanges)
        },
        statusDesc: {
            visible: isLoading,
            content: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.loading)}</span>)
        },
        viewAllChangesButton: {
            content: translator(messages.viewAllPolicyChanges),
            visible: isDashboard,
            onClick: onViewAllChangesButtonClicked,
            mlTagManagerLinkClick: "Dashboard_activityReport_viewAll_policyChanges"
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            renderDateCell,
            renderCustomerName,
            renderProductCell,
            renderPolicyCell,
            renderInitiatorCell,
            renderDescriptionCell
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

MLPolicyChangesReportComponent.propTypes = {
    showAgentChanges: PropTypes.bool.isRequired,
    showAllChanges: PropTypes.bool.isRequired,
    rowLimit: PropTypes.number.isRequired,
    isDashboard: PropTypes.bool.isRequired,
    isCompactView: PropTypes.bool.isRequired,
    getCustomerDisplayNameRevert: PropTypes.func.isRequired,
    history: PropTypes.shape({push: PropTypes.func}).isRequired,
}

export default withRouter(MLPolicyChangesReportComponent);
